import React, { useEffect, useState } from 'react';
import type { TeilnahmeErgebnis } from '../../../services/sifa/types';
import { formatiereDatum } from './teilnahmeSeiteLogic';
import type { ZertifikatStatus } from './useTeilnahme';
import { ALS_LINK, FEHLERKASTEN, HINWEISKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from './teilnahmeSeiteStil';

const ABSENDER = 'report@gefas-mbh.de';

/** Dateiname aus Content-Disposition; sonst aus der Nummer. */
const dateinameAus = (disposition: string | null, zertifikatNr: string | null | undefined): string => {
  const m = /filename="([^"]+)"/.exec(disposition || '');
  if (m) return m[1];
  return `Zertifikat_${(zertifikatNr || 'Unterweisung').replace(/[^\w.-]+/g, '_')}.pdf`;
};

/** Fehlertext aus einer Nicht-200-Antwort: JSON `error`, sonst Text, sonst der Status. */
const fehlerAus = async (antwort: Response): Promise<string> => {
  const roh = await antwort.text().catch(() => '');
  try {
    const json = JSON.parse(roh);
    if (json && typeof json.error === 'string' && json.error) return json.error;
  } catch {
    // kein JSON — der Rohtext (z. B. Vercels Funktionsfehler) sagt trotzdem etwas.
  }
  const text = roh.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200);
  return text ? `HTTP ${antwort.status}: ${text}` : `HTTP ${antwort.status}`;
};

/**
 * Letzte Karte. Bestanden: Haken, Zertifikat-Nr., Gueltigkeit, Versandhinweis;
 * der Versand wird beim Aufblenden angestossen (nie blockierend). Dazu der
 * Download des Zertifikats ueber denselben Endpunkt (modus 'download'):
 * bleibt die Mail aus, hat der Teilnehmer sein Blatt trotzdem in der Hand.
 * Nicht bestanden: Punkte und zwei Wege zurueck — Fragen erneut oder
 * Inhalte lesen. Kam der Teilnehmer ueber den Sammel-Link, fuehrt in beiden
 * Faellen „Zur Übersicht" zur Liste zurueck.
 */
export const ErgebnisAnsicht: React.FC<{
  /** `versucheRest` liefert die RPC nur bei nicht bestanden; nach einem Neuladen fehlt es. */
  ergebnis: TeilnahmeErgebnis & { versucheRest?: number | null };
  /** Sitzungstoken der Teilnahme — Schluessel fuer den Download; null = kein Download moeglich. */
  teilnahmeToken?: string | null;
  email: string;
  zertifikatStatus: ZertifikatStatus;
  busy: boolean;
  fehler: string | null;
  onZertifikatAnfordern: () => void;
  onNochmal: () => void;
  onInhalteLesen: () => void;
  /** Keine Versuche mehr in dieser Sitzung: alles auf Anfang. */
  onNeuBeginnen: () => void;
  /** Sammel-Link (`?von=`): Link zurueck zur Liste; null = kein Knopf. */
  uebersichtHref?: string | null;
}> = ({ ergebnis, teilnahmeToken = null, email, zertifikatStatus, busy, fehler, onZertifikatAnfordern, onNochmal, onInhalteLesen, onNeuBeginnen, uebersichtHref = null }) => {
  const [downloadLaeuft, setDownloadLaeuft] = useState(false);
  const [downloadFehler, setDownloadFehler] = useState<string | null>(null);

  useEffect(() => {
    if (ergebnis.bestanden) onZertifikatAnfordern();
    // Nur beim ersten Aufblenden — der Hook riegelt Doppelaufrufe selbst ab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ergebnis.teilnahmeId]);

  const herunterladen = async () => {
    if (!teilnahmeToken || downloadLaeuft) return;
    setDownloadLaeuft(true);
    setDownloadFehler(null);
    try {
      const antwort = await fetch('/api/unterweisung-zertifikat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teilnahmeToken, modus: 'download' }),
      });
      if (!antwort.ok) throw new Error(await fehlerAus(antwort));
      const blob = await antwort.blob();
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = dateinameAus(antwort.headers.get('Content-Disposition'), ergebnis.zertifikatNr);
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        // Nicht sofort freigeben: Safari/iOS starten den Download erst nach dem Klick-Handler.
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
    } catch (e) {
      setDownloadFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadLaeuft(false);
    }
  };

  if (ergebnis.bestanden) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 shrink-0"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <h2 className="text-2xl font-bold text-emerald-700">Bestanden</h2>
        </div>

        <p className="text-slate-700">
          Du hast {ergebnis.punkte} von {ergebnis.punkteMax} Punkten erreicht. Vielen Dank für deine Teilnahme.
        </p>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ergebnis.zertifikatNr && (
            <div className="p-3 bg-slate-50 rounded-xl">
              <dt className="text-sm text-slate-500">Zertifikat-Nr.</dt>
              <dd className="font-semibold break-all">{ergebnis.zertifikatNr}</dd>
            </div>
          )}
          {ergebnis.gueltigBis && (
            <div className="p-3 bg-slate-50 rounded-xl">
              <dt className="text-sm text-slate-500">Gültig bis</dt>
              <dd className="font-semibold">{formatiereDatum(ergebnis.gueltigBis)}</dd>
            </div>
          )}
        </dl>

        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 space-y-1" aria-live="polite">
          <p>
            Dein Zertifikat wird an <strong className="break-all">{email}</strong> gesendet (Absender {ABSENDER}).
          </p>
          {zertifikatStatus === 'folgt' && (
            <p className="text-sm">
              Der Versand ist noch nicht bestätigt — du kannst das Zertifikat hier herunterladen.
            </p>
          )}
          {zertifikatStatus === 'laeuft' && <p className="text-sm">Der Nachweis wird gerade erstellt…</p>}
        </div>

        {teilnahmeToken && (
          <div className="space-y-2">
            <button type="button" onClick={() => void herunterladen()} disabled={downloadLaeuft} className={KNOPF_SEKUNDAER}>
              {downloadLaeuft ? 'Zertifikat wird erstellt…' : 'Zertifikat herunterladen'}
            </button>
            {downloadFehler && (
              <div className={FEHLERKASTEN} role="alert">
                Der Download hat nicht geklappt: {downloadFehler}
              </div>
            )}
          </div>
        )}

        {uebersichtHref ? (
          <a href={uebersichtHref} className={`${KNOPF_PRIMAER} ${ALS_LINK}`}>
            Zur Übersicht
          </a>
        ) : (
          <p className="text-sm text-slate-500">Du kannst dieses Fenster jetzt schließen.</p>
        )}
      </div>
    );
  }

  // Die RPC zaehlt Fehlversuche je Sitzung (ab dem dritten: P0002). Ist
  // nichts mehr uebrig, fuehren „Nochmal" und „Inhalte lesen" nur in den
  // Fehler — dann steht hier gleich der Neustart.
  const rest = ergebnis.versucheRest;
  const keineVersucheMehr = rest !== null && rest !== undefined && rest <= 0;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-slate-900">Leider nicht bestanden</h2>
        <p className="text-slate-700">
          Du hast {ergebnis.punkte} von {ergebnis.punkteMax} Punkten erreicht.
          {keineVersucheMehr
            ? ' In dieser Sitzung sind keine Versuche mehr übrig.'
            : ' Das reicht noch nicht — du kannst es gleich noch einmal versuchen.'}
        </p>
        {rest !== null && rest !== undefined && rest > 0 && (
          <p className="text-sm font-semibold text-slate-600" aria-live="polite">
            {rest === 1 ? 'Noch 1 Versuch.' : `Noch ${rest} Versuche.`}
          </p>
        )}
      </div>

      {fehler && (
        <div className={FEHLERKASTEN} role="alert">
          {fehler}
        </div>
      )}

      {keineVersucheMehr ? (
        <>
          <div className={HINWEISKASTEN}>
            Beginne die Unterweisung neu und lies die Inhalte noch einmal in Ruhe — die Fragen kommen danach erneut.
          </div>
          <button type="button" onClick={onNeuBeginnen} disabled={busy} className={KNOPF_PRIMAER}>
            Neu beginnen
          </button>
        </>
      ) : (
        <>
          <div className={HINWEISKASTEN}>Tipp: Lies die Inhalte noch einmal in Ruhe, bevor du die Fragen erneut beantwortest.</div>

          <div className="flex flex-col gap-2">
            <button type="button" onClick={onNochmal} disabled={busy} className={KNOPF_PRIMAER}>
              {busy ? 'Einen Moment…' : 'Nochmal versuchen'}
            </button>
            <button type="button" onClick={onInhalteLesen} disabled={busy} className={KNOPF_SEKUNDAER}>
              Inhalte noch einmal lesen
            </button>
          </div>
        </>
      )}

      {uebersichtHref && (
        <a href={uebersichtHref} className={`${KNOPF_SEKUNDAER} ${ALS_LINK}`}>
          Zur Übersicht
        </a>
      )}
    </div>
  );
};
